import { useState } from 'react';
import useLocalStorage from '@/hooks/useLocalStorage';
import { defaultBanners, type AdminBanner } from '@/mocks/adminData';

const bannerLocations: { value: AdminBanner['location']; label: string }[] = [
  { value: 'principal', label: 'Banner principal' },
  { value: 'colecao', label: 'Banner de coleção' },
  { value: 'promocional', label: 'Banner promocional' },
  { value: 'final-whatsapp', label: 'Banner final WhatsApp' },
];

const emptyBanner: AdminBanner = {
  id: '', title: '', subtitle: '', buttonText: '', buttonLink: '#', image: '', location: 'principal', active: true,
};

function genId(list: AdminBanner[]): string {
  const max = list.reduce((acc, b) => { const n = parseInt(b.id.replace('ban-', ''), 10); return n > acc ? n : acc; }, 0);
  return `ban-${max + 1}`;
}

function BannersPage() {
  const [banners, setBanners] = useLocalStorage<AdminBanner[]>('admin_banners', defaultBanners);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<AdminBanner | null>(null);
  const [form, setForm] = useState<AdminBanner>(emptyBanner);
  const [deleteTarget, setDeleteTarget] = useState<AdminBanner | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const openNew = () => { setEditing(null); setForm({ ...emptyBanner, id: genId(banners) }); setShowModal(true); };
  const openEdit = (b: AdminBanner) => { setEditing(b); setForm({ ...b }); setShowModal(true); };

  const handleSave = () => {
    if (!form.title.trim()) return;
    setBanners(editing ? banners.map((b) => (b.id === form.id ? form : b)) : [...banners, form]);
    setShowModal(false); setEditing(null);
    setSavedFlash(true); setTimeout(() => setSavedFlash(false), 2000);
  };

  const handleDelete = () => { if (!deleteTarget) return; setBanners(banners.filter((b) => b.id !== deleteTarget.id)); setDeleteTarget(null); };

  const locLabel = (loc: string) => bannerLocations.find((l) => l.value === loc)?.label || loc;

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {savedFlash && (
        <div className="fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm font-medium shadow-sm">
          <i className="ri-check-line mr-1.5" />Alterações salvas com sucesso.
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">Banners</h1>
          <p className="text-sm text-foreground-500 mt-1">Gerencie os banners do site.</p>
        </div>
        <button type="button" onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap">
          <i className="ri-add-line" />Adicionar banner
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {banners.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-image-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm text-foreground-500 mb-1">Nenhum banner cadastrado</p>
            <button type="button" onClick={openNew} className="text-xs text-primary-600 hover:text-primary-700 font-medium">Adicionar primeiro banner</button>
          </div>
        ) : (
          banners.map((b) => (
            <div key={b.id} className="bg-white rounded-xl border border-background-200 overflow-hidden">
              <div className="aspect-[16/5] overflow-hidden">
                <img src={b.image} alt={b.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-heading text-sm font-semibold text-foreground-900">{b.title}</h3>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${b.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{b.active ? 'Ativo' : 'Inativo'}</span>
                </div>
                <p className="text-xs text-foreground-500 mb-1">{b.subtitle}</p>
                <p className="text-[10px] text-foreground-400 mb-3">{locLabel(b.location)}</p>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => openEdit(b)} className="flex-1 px-3 py-1.5 rounded-lg text-xs font-medium text-foreground-600 bg-background-100 hover:bg-background-200 transition-colors text-center">Editar</button>
                  <button type="button" onClick={() => setDeleteTarget(b)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors">
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-xl border border-background-200 w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-5">{editing ? 'Editar banner' : 'Novo banner'}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Local do banner</label>
                <select value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value as AdminBanner['location'] })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400">
                  {bannerLocations.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Título</label>
                <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Subtítulo</label>
                <input type="text" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">Texto do botão</label>
                  <input type="text" value={form.buttonText} onChange={(e) => setForm({ ...form, buttonText: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">Link do botão</label>
                  <input type="text" value={form.buttonLink} onChange={(e) => setForm({ ...form, buttonLink: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Imagem (URL)</label>
                <input type="text" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
                {form.image && <img src={form.image} alt="Preview" className="mt-2 w-full h-24 rounded-lg object-cover border border-background-200" />}
              </div>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-400" /><span className="text-sm text-foreground-700">Banner ativo</span></label>
            </div>
            <div className="flex items-center gap-3 mt-6 pt-4 border-t border-background-100">
              <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2.5 rounded-full text-sm font-medium text-foreground-600 bg-background-100 hover:bg-background-200 transition-colors whitespace-nowrap">Cancelar</button>
              <button type="button" onClick={handleSave} className="px-4 py-2.5 rounded-full text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors whitespace-nowrap">Salvar alterações</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-xl border border-background-200 w-full max-w-sm p-6">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3"><i className="ri-delete-bin-line text-lg text-red-500" /></div>
            <p className="text-sm text-foreground-700 text-center mb-1">Tem certeza que deseja excluir?</p>
            <p className="text-xs text-foreground-500 text-center mb-5">{deleteTarget.title}</p>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setDeleteTarget(null)} className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium text-foreground-600 bg-background-100 hover:bg-background-200 transition-colors whitespace-nowrap">Cancelar</button>
              <button type="button" onClick={handleDelete} className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors whitespace-nowrap">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BannersPage;